import axios from 'axios'

const state = {
  devices: [], // Currently displayed devices (filtered/paginated)
  allCachedDevices: [], // Full unfiltered device list from API
  totalCount: 0,
  registeredCount: 0,
  unregisteredCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
  lastUpdated: null,
  // Progress tracking for async queries
  inProgress: false,
  currentRequest: 0,
  message: '',
  // Cache metadata for UX
  fromCache: false,
  cacheAgeMinutes: 0,
  filters: {
    status: 'Unregistered', // Default to show problems
    search: '',
    page: 1,
    limit: 25
  }
}

const getters = {
  phoneStatusDevices: (state) => state.devices,
  phoneStatusCounts: (state) => ({
    total: state.totalCount,
    registered: state.registeredCount,
    unregistered: state.unregisteredCount
  }),
  phoneStatusLoading: (state) => state.loading,
  phoneStatusFilters: (state) => state.filters,
  phoneStatusPagination: (state) => ({
    page: state.filters.page,
    totalPages: state.totalPages,
    limit: state.filters.limit
  }),
  phoneStatusLastUpdated: (state) => state.lastUpdated,
  hasUnregisteredDevices: (state) => state.unregisteredCount > 0,
  // Progress tracking getters
  phoneStatusInProgress: (state) => state.inProgress,
  phoneStatusProgress: (state) => state.currentRequest,
  phoneStatusMessage: (state) => state.message,
  // Cache metadata getters
  phoneStatusFromCache: (state) => state.fromCache,
  phoneStatusCacheAge: (state) => state.cacheAgeMinutes
}

const mutations = {
  SET_PHONE_STATUS_DATA(state, data) {
    state.devices = data.devices || []
    // Store full cached device list when receiving from API
    if (data.devices && data.devices.length > 0) {
      state.allCachedDevices = data.devices
    }
    state.totalCount = data.totalCount || 0
    state.registeredCount = data.registeredCount || 0
    state.unregisteredCount = data.unregisteredCount || 0
    state.totalPages = data.totalPages || 0
    state.lastUpdated = data.timestamp || new Date().toISOString()
    // Update progress tracking fields
    state.inProgress = data.inProgress || false
    state.currentRequest = data.currentRequest || 0
    state.message = data.message || ''
    // Update cache metadata fields
    state.fromCache = data.fromCache || false
    state.cacheAgeMinutes = data.cacheAgeMinutes || 0
  },

  APPLY_FILTERS_TO_CACHE(state) {
    // Client-side filtering and pagination
    if (!state.allCachedDevices || state.allCachedDevices.length === 0) {
      state.devices = []
      state.totalPages = 0
      return
    }

    // Apply filters
    let filteredDevices = state.allCachedDevices.filter((device) => {
      // Status filter
      if (state.filters.status) {
        if (state.filters.status === 'Registered') {
          // Show only registered devices
          if (device.status && device.status.toLowerCase() !== 'registered') {
            return false
          }
        }
        if (state.filters.status === 'Unregistered') {
          // Show only unregistered devices
          if (device.status && device.status.toLowerCase() === 'registered') {
            return false
          }
        }
      }

      // Search filter
      if (state.filters.search) {
        const searchLower = state.filters.search.toLowerCase()
        const matches =
          (device.deviceName &&
            device.deviceName.toLowerCase().includes(searchLower)) ||
          (device.directoryNumber &&
            device.directoryNumber.toLowerCase().includes(searchLower)) ||
          (device.description &&
            device.description.toLowerCase().includes(searchLower))
        if (!matches) {
          return false
        }
      }

      return true
    })

    // Apply pagination
    const totalFiltered = filteredDevices.length
    state.totalPages = Math.ceil(totalFiltered / state.filters.limit)
    const startIndex = (state.filters.page - 1) * state.filters.limit
    const endIndex = startIndex + state.filters.limit

    state.devices = filteredDevices.slice(startIndex, endIndex)
  },

  SET_PHONE_STATUS_LOADING(state, loading) {
    state.loading = loading
  },

  SET_PHONE_STATUS_ERROR(state, error) {
    state.error = error
  },

  UPDATE_PHONE_STATUS_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters }
  },

  RESET_PHONE_STATUS_FILTERS(state) {
    state.filters = {
      status: 'Unregistered',
      search: '',
      page: 1,
      limit: 25
    }
  },

  SET_PHONE_STATUS_PAGE(state, page) {
    state.filters.page = page
  }
}

const actions = {
  async fetchPhoneStatus({ commit, state }, filters = {}) {
    commit('SET_PHONE_STATUS_LOADING', true)
    commit('SET_PHONE_STATUS_ERROR', null)

    // Merge provided filters with current state filters
    const queryFilters = {
      ...state.filters,
      ...filters
    }
    console.log('fetchPhoneStatus called with:', {
      providedFilters: filters,
      stateFilters: state.filters,
      mergedFilters: queryFilters
    })

    // Update filters in state
    if (Object.keys(filters).length > 0) {
      commit('UPDATE_PHONE_STATUS_FILTERS', filters)
    }

    try {
      const params = new URLSearchParams()

      // Simple: just send what we're given
      params.append('status', queryFilters.status || '')
      if (queryFilters.search) params.append('search', queryFilters.search)
      params.append('page', queryFilters.page || 1)
      params.append('limit', queryFilters.limit || 25)
      if (queryFilters.forceRefresh) params.append('forceRefresh', 'true')

      const response = await axios.get(
        `/phone-registration-status?${params.toString()}`
      )

      if (response.data) {
        commit('SET_PHONE_STATUS_DATA', response.data)
      }

      return response.data
    } catch (error) {
      console.error('Error fetching phone status:', error)
      commit(
        'SET_PHONE_STATUS_ERROR',
        error.message || 'Failed to fetch phone status'
      )
      throw error
    } finally {
      commit('SET_PHONE_STATUS_LOADING', false)
    }
  },

  async refreshPhoneStatus({ dispatch, state }) {
    return dispatch('fetchPhoneStatus', state.filters)
  },

  updatePhoneStatusFilters({ commit }, filters) {
    // Reset page to 1 when filters change (except for page changes)
    if (filters.status !== undefined || filters.search !== undefined) {
      filters.page = 1
    }
    commit('UPDATE_PHONE_STATUS_FILTERS', filters)
    // Apply filters client-side - NO API call
    commit('APPLY_FILTERS_TO_CACHE')
  },

  setPhoneStatusPage({ commit }, page) {
    commit('SET_PHONE_STATUS_PAGE', page)
    // Apply pagination client-side - NO API call
    commit('APPLY_FILTERS_TO_CACHE')
  },

  resetPhoneStatusFilters({ commit }) {
    commit('RESET_PHONE_STATUS_FILTERS')
    // Apply filters client-side - NO API call
    commit('APPLY_FILTERS_TO_CACHE')
  },

  async checkPhoneStatusOnly({ commit }) {
    // Status check only - NEVER starts a query
    // Safe to call on page load
    try {
      const response = await axios.get('/phone-registration-status/status')

      if (response.data) {
        commit('SET_PHONE_STATUS_DATA', response.data)
      }

      return response.data
    } catch (error) {
      console.error('Error checking phone status:', error)
      return null
    }
  },

  async exportPhoneStatus({ state }) {
    try {
      const params = new URLSearchParams()
      if (state.filters.status) params.append('status', state.filters.status)
      if (state.filters.search) params.append('search', state.filters.search)
      params.append('page', 1)
      params.append('limit', 10000) // Get all for export

      const response = await axios.get(
        `/phone-registration-status?${params.toString()}`
      )

      if (response.data && response.data.devices) {
        // Convert to CSV
        const csvContent = convertToCSV(response.data.devices)
        downloadCSV(
          csvContent,
          `phone-status-${new Date().toISOString().split('T')[0]}.csv`
        )
      }
    } catch (error) {
      console.error('Error exporting phone status:', error)
      throw error
    }
  }
}

// Helper functions for CSV export
function convertToCSV(devices) {
  const headers = [
    'Device Name',
    'Directory Number',
    'Status',
    'IP Address',
    'Node',
    'Timestamp',
    'Description'
  ]
  const rows = devices.map((device) => [
    device.deviceName || '',
    device.directoryNumber || '',
    device.status || '',
    device.ipAddress || '',
    device.node || '',
    device.timestamp || '',
    device.description || ''
  ])

  const csvRows = [
    headers.join(','),
    ...rows.map((row) => row.map((field) => `"${field}"`).join(','))
  ]

  return csvRows.join('\n')
}

function downloadCSV(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

export default {
  state,
  getters,
  mutations,
  actions
}
